/**
 * 认证状态管理 — Zustand store。
 * 管理 JWT token + 当前用户信息 + 登录/登出 action。
 * Token 持久化到 localStorage，应用初始化时自动恢复。
 */
import { create } from "zustand";
import type { Account } from "../types";

/** 认证状态接口。 */
interface AuthState {
  /** Access Token（JWT）。 */
  token: string | null;
  /** Refresh Token。 */
  refreshToken: string | null;
  /** 当前登录账户信息。 */
  account: Account | null;
  /** 登录 action。 */
  login: (token: string, refreshToken: string, account: Account) => void;
  /** 登出 action。 */
  logout: () => void;
  /** 从 localStorage 恢复 token。 */
  restore: () => void;
}

/** localStorage 键名。 */
const TOKEN_KEY = "geo_access_token";
const REFRESH_KEY = "geo_refresh_token";
const ACCOUNT_KEY = "geo_account_info";

/** 从 localStorage 读取初始状态（页面刷新时自动恢复登录态）。 */
function getInitialState(): {
  token: string | null;
  refreshToken: string | null;
  account: Account | null;
} {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const accountStr = localStorage.getItem(ACCOUNT_KEY);
    const account = accountStr ? (JSON.parse(accountStr) as Account) : null;
    return { token, refreshToken, account };
  } catch {
    return { token: null, refreshToken: null, account: null };
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  ...getInitialState(),

  login: (token: string, refreshToken: string, account: Account) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_KEY, refreshToken);
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
    set({ token, refreshToken, account });
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
    set({ token: null, refreshToken: null, account: null });
  },

  restore: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const accountStr = localStorage.getItem(ACCOUNT_KEY);
    const account = accountStr ? (JSON.parse(accountStr) as Account) : null;
    if (token) {
      set({ token, refreshToken, account });
    }
  },
}));
